const API_URL =
  import.meta.env.VITE_API_URL ||
  import.meta.env.NEXT_PUBLIC_API_URL ||
  "http://localhost:8000/api/v1";

type RequestOptions = RequestInit & {
  query?: Record<string, string | number | boolean | undefined | null>;
};

function buildUrl(path: string, query?: RequestOptions["query"]) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${API_URL}${normalizedPath}`);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url.toString();
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { query, headers, ...requestOptions } = options;
  const response = await fetch(buildUrl(path, query), {
    ...requestOptions,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  return response.json() as Promise<T>;
}

export interface DashboardSummaryApi {
  total_projects: number;
  total_trees: number;
  total_species: number;
  total_documents: number;
  total_parties: number;
  total_rrr: number;
  total_validation_issues: number;
  total_point_cloud_assets: number;
  total_environmental_units: number;
  total_carbon_projects: number;
  status: string;
}

export interface SpeciesApi {
  id: number;
  uuid: string;
  family_id: number | null;
  family_name: string | null;
  popular_name: string;
  scientific_name: string;
  origin: string | null;
  tree_size: string | null;
  functional_group: string | null;
  wood_density: number | null;
  preliminary_carbon_factor: number | null;
  protected_species: boolean;
  invasive_species: boolean;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  registered_tree_count: number;
}

export interface ProjectApi {
  project_id: number;
  project_uuid: string;
  tenant_id: number;
  client_id: number | null;
  municipality_id: number | null;
  name: string;
  project_type: string;
  status: string;
  total_trees: number;
  active_trees: number;
  high_risk_trees: number;
  species_count: number;
  inspection_count: number;
  latest_inspection_date: string | null;
  progress_percent: number;
  created_at: string;
  updated_at: string;
}

export interface TreeApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number | null;
  project_name: string | null;
  spatial_unit_id: number | null;
  spatial_unit_type: string | null;
  spatial_unit_name: string | null;
  species_id: number | null;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  protected_species: boolean | null;
  invasive_species: boolean | null;
  botanical_family: string | null;
  cta_code: string | null;
  public_code: string | null;
  individual_type: string | null;
  status: string;
  review_status: string | null;
  latitude: number | null;
  longitude: number | null;
  municipality_id: number | null;
  municipality_name: string | null;
  municipality_uf: string | null;
  neighborhood: string | null;
  street: string | null;
  address_reference: string | null;
  latest_inspection_id: number | null;
  latest_inspection_type: string | null;
  latest_inspection_date: string | null;
  latest_risk_assessment_id: number | null;
  latest_risk_class: string | null;
  latest_risk_score: number | null;
  latest_risk_priority: string | null;
  created_at: string;
  updated_at: string;
}

export interface TreesFeatureCollectionApi {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    id: number;
    geometry: { type: string; coordinates: unknown };
    properties: Record<string, unknown>;
  }>;
}

export interface InspectionApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number;
  project_name: string;
  tree_id: number;
  cta_code: string | null;
  public_code: string | null;
  species_id: number | null;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  inspection_type: string;
  inspection_date: string;
  technician_party_id: number | null;
  technician_name: string | null;
  source_id: number | null;
  source_title: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number | null;
  project_name: string | null;
  source_id: number | null;
  title: string;
  document_type: string;
  document_number: string | null;
  issuing_body: string | null;
  issue_date: string | null;
  expiration_date: string | null;
  status: string;
  description: string | null;
  linked_entity_schema: string | null;
  linked_entity_table: string | null;
  linked_entity_id: number | null;
  linked_relation_type: string | null;
  linked_entity_name: string | null;
  file_count: number | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  file_path: string | null;
  created_at: string;
  updated_at: string;
}

export interface PartyApi {
  id: number;
  uuid: string;
  tenant_id: number;
  party_type: string;
  name: string;
  legal_name: string | null;
  document_number: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  municipality_id: number | null;
  municipality_name: string | null;
  uf: string | null;
  status: string;
  total_roles: number;
  project_link_count: number;
  spatial_unit_link_count: number;
  document_count: number;
  role_types: string | null;
  created_at: string;
  updated_at: string;
}

export interface QualitySummaryApi {
  tenant_id: number;
  project_id: number;
  project_name: string;
  open_issue_count: number;
  critical_issue_count: number;
  high_issue_count: number;
  medium_issue_count: number;
  estimated_quality_score: number;
}

export interface ValidationIssueApi {
  tenant_id: number;
  project_id: number | null;
  rule_code: string;
  severity: string;
  entity_schema: string;
  entity_table: string;
  entity_id: number;
  message: string;
  detected_at: string;
}

export interface TreeMetrics3DApi {
  id: number | null;
  uuid: string | null;
  tenant_id: number;
  project_id: number | null;
  point_cloud_name: string | null;
  point_cloud_id: number | null;
  tree_id: number;
  cta_code: string | null;
  public_code: string | null;
  species_popular_name: string | null;
  species_scientific_name: string | null;
  field_inspection_id: number | null;
  field_dap_cm: number | null;
  field_height_m: number | null;
  field_crown_diameter_m: number | null;
  field_crown_area_m2: number | null;
  field_measured_at: string | null;
  height_3d_m: number | null;
  crown_diameter_3d_m: number | null;
  crown_area_3d_m2: number | null;
  crown_volume_3d_m3: number | null;
  inclination_3d_deg: number | null;
  height_delta_m: number | null;
  crown_diameter_delta_m: number | null;
  confidence_level: string | null;
  validation_status: string;
  extracted_at: string | null;
}

export const api = {
  health: () => apiFetch("/health"),
  dashboardSummary: () => apiFetch<DashboardSummaryApi>("/dashboard/summary"),
  species: () => apiFetch<SpeciesApi[]>("/species"),
  projects: () => apiFetch<ProjectApi[]>("/projects"),
  trees: (projectId?: number) => apiFetch<TreeApi[]>("/trees", { query: { project_id: projectId } }),
  treesGeoJson: (projectId?: number) =>
    apiFetch<TreesFeatureCollectionApi>("/trees/geojson", { query: { project_id: projectId } }),
  inspections: (projectId?: number) =>
    apiFetch<InspectionApi[]>("/inspections", { query: { project_id: projectId } }),
  documents: (projectId?: number) => apiFetch<DocumentApi[]>("/documents", { query: { project_id: projectId } }),
  parties: () => apiFetch<PartyApi[]>("/parties"),
  qualitySummary: () => apiFetch<QualitySummaryApi[]>("/quality/summary"),
  qualityIssues: (projectId?: number, severity?: string) =>
    apiFetch<ValidationIssueApi[]>("/quality/issues", { query: { project_id: projectId, severity } }),
  pointCloudTreeMetrics: (projectId?: number) =>
    apiFetch<TreeMetrics3DApi[]>("/point-cloud/tree-metrics", { query: { project_id: projectId } }),
  areasPiloto: () => apiFetch<AreaPilotoApi[]>("/areas-piloto"),
  quadras: (areaPilotoId?: number) => apiFetch<QuadraApi[]>("/quadras", { query: { area_piloto_id: areaPilotoId } }),
  inspectionsWithQuadras: () => apiFetch<InspectionQuadraLinkApi[]>("/territorial/inspections-with-quadras"),
  territorialInspections: () => apiFetch<TerritorialInspectionSummaryApi[]>("/territorial-inspections"),
  territorialInspectionDetail: (inspectionCode: string) =>
    apiFetch<TerritorialInspectionDetailApi>(`/territorial-inspections/${encodeURIComponent(inspectionCode)}`),
  territorialInspectionObjects: (inspectionCode: string) =>
    apiFetch<DetectedObjectApi[]>(`/territorial-inspections/${encodeURIComponent(inspectionCode)}/objects`),
  territorialInspectionFieldValidationNotes: (inspectionCode: string) =>
    apiFetch<FieldValidationNoteApi[]>(
      `/territorial-inspections/${encodeURIComponent(inspectionCode)}/field-validation-notes`
    ),
  territorialIndicatorsSummary: () => apiFetch<TerritorialIndicatorsSummaryApi>("/territorial-indicators/summary"),
};

export function territorialInspectionImageUrl(inspectionCode: string): string {
  return buildUrl(`/territorial-inspections/${encodeURIComponent(inspectionCode)}/image`);
}

// --- Território > Área piloto / Quadras (camada operacional real) --------

export interface GeoJSONGeometryApi {
  type: string;
  coordinates?: unknown;
  geometries?: GeoJSONGeometryApi[];
}

export interface AreaPilotoApi {
  id: number;
  uuid: string;
  tenant_id: number;
  project_id: number | null;
  municipality_id: number | null;
  municipality_name: string | null;
  uf: string | null;
  name: string;
  code: string | null;
  neighborhood: string | null;
  expected_block_count: number | null;
  total_quadras: number;
  total_trees: number;
  total_postes: number;
  total_redes_aereas: number;
  total_conflitos_alta_critica: number;
  area_ha: number | null;
  geom: GeoJSONGeometryApi | null;
  status: string;
  updated_at: string;
}

export interface QuadraApi {
  id: number;
  uuid: string;
  tenant_id: number;
  area_piloto_id: number;
  area_piloto_name: string | null;
  spatial_unit_id: number | null;
  code: string;
  name: string | null;
  neighborhood: string | null;
  area_m2: number | null;
  perimeter_m: number | null;
  total_trees: number;
  total_individuos_arboreos: number;
  total_postes: number;
  total_redes_aereas: number;
  total_interferencias: number;
  total_interferencias_criticas: number;
  area_permeavel_m2: number;
  area_impermeavel_m2: number;
  geom: GeoJSONGeometryApi | null;
  status: string;
  updated_at: string;
}

export interface InspectionQuadraLinkApi {
  inspection_id: number;
  inspection_uuid: string;
  inspection_code: string;
  tenant_id: number;
  project_id: number | null;
  inspection_area_piloto_id: number | null;
  quadra_id: number | null;
  quadra_uuid: string | null;
  quadra_area_piloto_id: number | null;
  quadra_code: string | null;
  quadra_name: string | null;
  inspection_block_code: string | null;
  inspection_neighborhood: string | null;
  quadra_neighborhood: string | null;
  inspection_status: string;
  processing_status: string;
  field_validation_status: string;
  ai_model: string | null;
  analysis_version: string | null;
  inspection_date: string;
  image_filename: string | null;
  confidence_score: number | null;
  link_method: string;
  link_confidence: string;
  total_code_matches: number;
  tree_canopy_percent: number | null;
  impervious_area_percent: number | null;
  heat_island_risk: string | null;
  tree_deficit_level: string | null;
  estimated_tree_count: number | null;
  planting_potential_count: number | null;
  inventory_canopy_coverage_percent: number | null;
  quadra_area_m2: number | null;
  quadra_perimeter_m: number | null;
  geom: GeoJSONGeometryApi | null;
  created_at: string;
  updated_at: string;
}

// --- Território > Inspeções ---------------------------------------------

export interface TerritorialInspectionSummaryApi {
  tenant_id: number;
  neighborhood: string | null;
  block_code: string | null;
  inspection_id: number;
  uuid: string;
  inspection_code: string;
  previous_inspection_id: number | null;
  previous_inspection_code: string | null;
  inspection_date: string;
  source_type: string;
  source_reference: string | null;
  image_uri: string | null;
  image_filename: string | null;
  responsible_party_id: number | null;
  responsible_party_name: string | null;
  processing_status: string;
  data_classification: string;
  intended_use: string;
  field_validation_required: boolean;
  field_validation_status: string;
  status: string;
  confidence_score: number | null;
  estimated_tree_count: number | null;
  canopy_coverage_percent: number | null;
  planting_potential_count: number | null;
  tree_deficit_count: number | null;
  risk_tree_count: number | null;
  field_validation_notes_count: number;
  created_at: string;
  updated_at: string;
}

export interface TerritorialRoadApi {
  road_name: string | null;
  road_type: string | null;
  pavement_type: string | null;
  pavement_condition: string | null;
  sidewalk_condition: string | null;
  drainage_condition: string | null;
  width_m: number | null;
  planting_potential_count: number | null;
  tree_conflict_count: number | null;
  notes: string | null;
}

export interface TerritorialLandUseApi {
  land_use_type: string;
  area_percent: number | null;
  area_m2: number | null;
  intensity_level: string | null;
  notes: string | null;
}

export interface TerritorialCoverageApi {
  coverage_type: string;
  area_percent: number | null;
  area_m2: number | null;
  permeability_class: string | null;
  heat_island_weight: number | null;
  confidence_score: number | null;
  notes: string | null;
}

export interface TerritorialTreeInventorySummaryApi {
  estimated_tree_count: number | null;
  adult_tree_count: number | null;
  young_tree_count: number | null;
  palm_count: number | null;
  shrub_count: number | null;
  canopy_coverage_percent: number | null;
  estimated_canopy_area_m2: number | null;
  planting_potential_count: number | null;
  tree_deficit_count: number | null;
  risk_tree_count: number | null;
  species_notes: string | null;
  notes: string | null;
}

export interface TerritorialIndicatorApi {
  indicator_code: string;
  indicator_name: string | null;
  category: string | null;
  value_numeric: number | null;
  value_text: string | null;
  unit: string | null;
  risk_level: string | null;
  confidence_score: number | null;
  notes: string | null;
}

export interface TerritorialRecommendationApi {
  recommendation_type: string;
  priority: string;
  description: string;
  target_location: string | null;
  estimated_quantity: number | null;
  expected_impact: string | null;
  status: string;
}

export interface TerritorialInspectionDetailApi extends TerritorialInspectionSummaryApi {
  report_title: string | null;
  report_text: string | null;
  roads: TerritorialRoadApi[];
  land_use: TerritorialLandUseApi[];
  coverage: TerritorialCoverageApi[];
  tree_inventory_summary: TerritorialTreeInventorySummaryApi | null;
  indicators: TerritorialIndicatorApi[];
  recommendations: TerritorialRecommendationApi[];
}

export interface DetectedObjectApi {
  id: number;
  uuid: string;
  tenant_id: number;
  inspection_code: string;
  block_code: string | null;
  neighborhood: string | null;
  inspection_id: number;
  object_code: string | null;
  object_type: string;
  object_label: string | null;
  confidence_score: number | null;
  detection_method: string | null;
  source_object_id: string | null;
  height_estimated_m: number | null;
  crown_diameter_estimated_m: number | null;
  canopy_area_m2: number | null;
  condition_status: string | null;
  risk_level: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface FieldValidationNoteApi {
  id: number;
  uuid: string;
  tenant_id: number;
  inspection_code: string;
  block_code: string | null;
  neighborhood: string | null;
  inspection_id: number;
  note_type: string;
  description: string;
  priority: string;
  status: string;
  responsible_party_id: number | null;
  responsible_party_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface TerritorialIndicatorsSummaryApi {
  avg_tree_coverage_by_neighborhood: Array<{
    tenant_id: number;
    neighborhood: string | null;
    total_inspections: number;
    avg_canopy_coverage_percent: number | null;
    avg_estimated_tree_count: number | null;
  }>;
  heat_island_ranking: Array<{
    tenant_id: number;
    neighborhood: string | null;
    block_code: string | null;
    inspection_id: number;
    heat_island_risk_score: number | null;
    risk_level: string | null;
    updated_at: string;
  }>;
  estimated_trees_by_block: Array<{
    tenant_id: number;
    neighborhood: string | null;
    block_code: string | null;
    inspection_id: number;
    estimated_tree_count: number;
    risk_tree_count: number;
    planting_potential_count: number;
  }>;
  planting_potential_by_road: Array<{
    tenant_id: number;
    road_name: string | null;
    road_segments: number;
    total_planting_potential: number;
  }>;
  avg_impermeabilization: Array<{
    tenant_id: number;
    neighborhood: string | null;
    avg_impermeabilization_percent: number | null;
  }>;
  tree_deficit_ranking: Array<{
    tenant_id: number;
    neighborhood: string | null;
    block_code: string | null;
    inspection_id: number;
    tree_deficit_count: number;
    planting_potential_count: number;
    canopy_coverage_percent: number;
  }>;
}

// --- Editor Territorial WebGIS -------------------------------------------

export interface RascunhoFeicaoApi {
  id: string;
  projeto_id: string;
  quadra_id: string | null;
  projeto_ref_id: number | null;
  quadra_ref_id: number | null;
  area_piloto_ref_id: number | null;
  camada_destino: string;
  tipo_objeto: string;
  tipo_geometria: string;
  geom: GeoJSONGeometryApi | null;
  propriedades: Record<string, unknown>;
  status: string;
  fonte: string;
  validacao: string;
  area_m2: number | null;
  comprimento_m: number | null;
  perimetro_m: number | null;
  publicado_schema: string | null;
  publicado_tabela: string | null;
  publicado_id: string | null;
  criado_por: string | null;
  revisado_por: string | null;
  aprovado_por: string | null;
  publicado_por: string | null;
  criado_em: string;
  atualizado_em: string;
  revisado_em: string | null;
  aprovado_em: string | null;
  publicado_em: string | null;
}

export interface RascunhoFeicaoCreatePayload {
  projeto_id: string;
  quadra_id?: string | null;
  projeto_ref_id?: number | null;
  quadra_ref_id?: number | null;
  area_piloto_ref_id?: number | null;
  camada_destino: string;
  tipo_objeto: string;
  geom: GeoJSONGeometryApi;
  propriedades?: Record<string, unknown>;
  criado_por?: string | null;
}

export interface RascunhoFeicaoUpdatePayload {
  geom?: GeoJSONGeometryApi;
  propriedades?: Record<string, unknown>;
}

export interface RascunhoStatusActionPayload {
  usuario_id?: string | null;
  motivo?: string | null;
}

export interface RascunhoPublishResultApi {
  rascunho_id: string;
  camada_destino: string;
  publicado_id: string;
  objeto_territorial_id: string;
  status: string;
}

export interface CamadaGeoApi {
  id: string;
  nome: string;
  schema_name: string;
  table_name: string;
  camada_destino: string;
  tipo_objeto: string;
  tipo_geometria: string;
  descricao: string | null;
  status: string;
}

export interface CamadaFeatureApi {
  id: string;
  rascunho_id: string | null;
  quadra_id: string | null;
  geom: GeoJSONGeometryApi | null;
  propriedades: Record<string, unknown>;
  fonte: string;
  validacao: string;
  publicado_em: string;
}

export const editorApi = {
  createRascunho: (payload: RascunhoFeicaoCreatePayload) =>
    apiFetch<RascunhoFeicaoApi>("/editor/rascunhos", { method: "POST", body: JSON.stringify(payload) }),
  rascunhos: (filters?: { status?: string; quadra_ref_id?: number; projeto_ref_id?: number; camada_destino?: string }) =>
    apiFetch<RascunhoFeicaoApi[]>("/editor/rascunhos", { query: filters }),
  rascunho: (id: string) => apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}`),
  updateRascunho: (id: string, payload: RascunhoFeicaoUpdatePayload) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteRascunho: (id: string) =>
    apiFetch<void>(`/editor/rascunhos/${encodeURIComponent(id)}`, { method: "DELETE" }),
  enviarRevisao: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/enviar-revisao`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  aprovar: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/aprovar`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  rejeitar: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/rejeitar`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  solicitarAjuste: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoFeicaoApi>(`/editor/rascunhos/${encodeURIComponent(id)}/solicitar-ajuste`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  publicar: (id: string, payload: RascunhoStatusActionPayload = {}) =>
    apiFetch<RascunhoPublishResultApi>(`/editor/rascunhos/${encodeURIComponent(id)}/publicar`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  exportGeoJson: (filters?: { status?: string; quadra_ref_id?: number }) =>
    apiFetch<{ type: string; features: unknown[] }>("/editor/export/geojson", { query: filters }),
  camadas: () => apiFetch<CamadaGeoApi[]>("/editor/camadas"),
  camadaFeatures: (camadaId: string) =>
    apiFetch<CamadaFeatureApi[]>(`/editor/camadas/${encodeURIComponent(camadaId)}/features`),
};

// --- Settings ---------------------------------------------------------------

export interface SettingResponse {
  key: string;
  value: Record<string, unknown>;
}

export const settingsApi = {
  get: (key: string) =>
    apiFetch<SettingResponse>(`/settings/${encodeURIComponent(key)}`),
  put: (key: string, value: Record<string, unknown>) =>
    apiFetch<SettingResponse>(`/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),
};

